<template>
  <div class="dependent-coverage-assignment">
    <a-skeleton active :loading="loading_data">
      <a-card>
        <a-typography-title :level="4">
          Asignación de coberturas por carga
        </a-typography-title>
        <a-alert
          type="info"
          show-icon
          message="Debes seleccionar al menos una cobertura por cada carga."
          class="mb-3"
        />

        <a-empty v-if="assignments.length === 0">
          <template #description>
            <span>No has agregado cargas para asignar coberturas</span>
          </template>
        </a-empty>

        <a-card
          v-for="group in coverageGroups"
          :key="group.key"
          size="small"
          class="coverage-group-card mb-3"
        >
          <template #title>
            Póliza #{{ group.policyNumber }} · Grupo {{ group.groupName }}
          </template>

          <div class="coverage-matrix">
            <div
              v-for="coverage in group.coverages"
              :key="coverage.id"
              class="coverage-matrix__row"
              :style="matrixGridStyle"
            >
              <div class="coverage-matrix__coverage">
                <span>{{ coverage.name }}</span>
                <a-tag v-if="coverage.required" color="blue">Requerida</a-tag>
              </div>
              <div
                v-for="assignment in assignments"
                :key="assignment.key"
                class="coverage-matrix__dependent"
              >
                <a-checkbox
                  :checked="isCoverageSelected(assignment, coverage)"
                  :disabled="
                    coverage.required ||
                    !isCoverageAvailable(assignment, coverage)
                  "
                  @change="
                    (event) =>
                      toggleCoverage(assignment, coverage, event.target.checked)
                  "
                >
                  {{ assignment.name || 'Carga' }}
                </a-checkbox>
              </div>
            </div>
          </div>
        </a-card>

        <a-alert
          v-if="validationError"
          type="error"
          show-icon
          message="Debe seleccionar al menos una cobertura por cada carga."
        />
      </a-card>
    </a-skeleton>

    <a-row type="flex" justify="space-between" class="mt-3">
      <a-button size="large" @click="formSubmit(3)" :loading="loading">
        <template #icon>
          <ArrowLeftOutlined />
        </template>
        Atrás
      </a-button>
      <a-button
        size="large"
        type="primary"
        @click="formSubmit(5)"
        :loading="loading"
      >
        Siguiente
      </a-button>
    </a-row>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, defineExpose } from 'vue';
import { useStore } from 'vuex';
import { ArrowLeftOutlined } from '@ant-design/icons-vue';
import notification from '@/utils/notifications';
import {
  ACTION_CHANGE_STEP,
  FETCH_DEPENDENT_COVERAGES,
  PUT_DEPENDENT_COVERAGES
} from '../../store/types';
import { normalizeDependentCoverageAssignments } from './dependent-coverage-assignment-helpers';

const store = useStore();
const assignments = ref([]);
const loading_data = ref(false);
const loading = ref(false);
const validationError = ref(false);

const clearValidationError = () => {
  validationError.value = false;
};

const coverageGroups = computed(() => {
  const groups = new Map();

  assignments.value.forEach((assignment) => {
    assignment.eligibleCoverages.forEach((coverage) => {
      const policyKey = coverage.policyId ?? 'policy';
      const groupKey = coverage.groupCompanyId ?? 'group';
      const key = `${policyKey}-${groupKey}`;

      if (!groups.has(key)) {
        groups.set(key, {
          key,
          policyNumber: coverage.policyNumber || coverage.policyId || '-',
          groupName: coverage.groupName || coverage.groupCompanyId || '-',
          coverages: []
        });
      }

      const group = groups.get(key);
      if (!group.coverages.some((item) => item.id === coverage.id)) {
        group.coverages.push(coverage);
      }
    });
  });

  return Array.from(groups.values());
});

const matrixGridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${Math.max(
    assignments.value.length + 1,
    2
  )}, minmax(0, 1fr))`
}));

const isCoverageAvailable = (assignment, coverage) =>
  assignment.eligibleCoverages.some((item) => item.id === coverage.id);

const selectedCoverageIdsFor = (assignment) => {
  const requiredCoverageIds = assignment.eligibleCoverages
    .filter((coverage) => coverage.required)
    .map((coverage) => coverage.id);

  return [
    ...new Set([...assignment.selectedCoverageIds, ...requiredCoverageIds])
  ];
};

const isCoverageSelected = (assignment, coverage) =>
  selectedCoverageIdsFor(assignment).includes(coverage.id);

const toggleCoverage = (assignment, coverage, checked) => {
  if (coverage.required || !isCoverageAvailable(assignment, coverage)) return;

  assignment.selectedCoverageIds = checked
    ? [...new Set([...assignment.selectedCoverageIds, coverage.id])]
    : assignment.selectedCoverageIds.filter((id) => id !== coverage.id);
  clearValidationError();
};

const hasMissingCoverage = () =>
  assignments.value.some(
    (assignment) => selectedCoverageIdsFor(assignment).length === 0
  );

const saveAssignments = async (step) => {
  loading.value = true;
  try {
    await store.dispatch(
      PUT_DEPENDENT_COVERAGES,
      assignments.value.map((assignment) => ({
        insuredId: assignment.insuredId,
        selectedCoverageIds: selectedCoverageIdsFor(assignment)
      }))
    );
    store.dispatch(ACTION_CHANGE_STEP, step);
  } catch (error) {
    notification(
      'error',
      'Error',
      'Ha ocurrido un error al guardar las coberturas.'
    );
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const formSubmit = async (step) => {
  const missingCoverage = hasMissingCoverage();
  validationError.value = missingCoverage;

  // Going back remains available while a draft is incomplete. Advancing to
  // beneficiaries requires an explicit assignment for every current-form load.
  if (missingCoverage && step > 4) {
    return;
  }

  await saveAssignments(step);
};

const getAssignments = async () => {
  loading_data.value = true;
  try {
    const response = await store.dispatch(FETCH_DEPENDENT_COVERAGES);
    assignments.value = normalizeDependentCoverageAssignments(response);
  } catch (error) {
    notification(
      'error',
      'Error',
      'Ha ocurrido un error al obtener las coberturas.'
    );
    console.error(error);
  } finally {
    loading_data.value = false;
  }
};

onMounted(() => {
  getAssignments();
});

defineExpose({ formSubmit });
</script>

<style scoped>
.dependent-coverage-assignment {
  width: 100%;
}

.coverage-group-card :deep(.ant-card-body) {
  padding: 0 8px;
}

.coverage-matrix {
  overflow-x: auto;
}

.coverage-matrix__row {
  display: grid;
  min-width: 100%;
  border-bottom: 1px solid #f0f0f0;
}

.coverage-matrix__row:last-child {
  border-bottom: 0;
}

.coverage-matrix__coverage,
.coverage-matrix__dependent {
  display: flex;
  align-items: center;
  min-height: 50px;
  padding: 8px;
}

.coverage-matrix__coverage {
  gap: 8px;
}

.coverage-matrix__dependent {
  justify-content: flex-start;
}

@media (max-width: 767px) {
  .coverage-matrix__row {
    min-width: max-content;
  }

  .coverage-matrix__coverage,
  .coverage-matrix__dependent {
    min-width: 160px;
  }
}
</style>
